import type { ReactNode } from 'react';
import type { JoeConnectionStatusQuery } from '@packages/gql/generated/shopping/graphql';
import type { JoePresentationState } from './JoeOverview.types';

/** The status union as this operation selects it. */
export type JoeStatusResult = JoeConnectionStatusQuery['customerLoyaltyConnectionStatus'];

/**
 * Failures that mean "this customer must not see jö at all" rather than "something broke".
 *
 * `NOT_SUPPORTED` is what the unified loyalty resolver returns for a tenant without a jö plugin,
 * and the two auth failures mean the session is not (or no longer) entitled to ask. None of them is
 * something a customer can act on, so the card stays hidden instead of showing an error.
 */
const INELIGIBLE_FAILURE_TYPES: readonly string[] = [
  'NOT_SUPPORTED',
  'UNAUTHENTICATED',
  'UNAUTHORIZED',
];

export type ResolveJoeStateInput = {
  /** Whether the feature is available at all, already resolved by the caller. */
  isGateOpen: boolean;
  /** Transport- or GraphQL-level failure of the status query. */
  hasTransportError: boolean;
  /** A status refetch is in flight over an already resolved - and therefore stale - result. */
  isStatusRefreshing: boolean;
  /** A returning authorization code has been seen but not yet resolved into a connection. */
  isReturnPending: boolean;
  /** Status payload, `undefined` while the query is paused or has not resolved yet. */
  result: JoeStatusResult | undefined;
  /** The establish mutation of a returning SSO round-trip is in flight. */
  isEstablishing: boolean;
  /** The establish mutation of this page view failed. */
  hasEstablishFailed: boolean;
  /**
   * Copy for the two notification slots. Supplied by the caller so this module stays free of
   * `react-intl` and remains a plain unit-testable function.
   */
  notifications: {
    statusError: ReactNode;
    connectError: ReactNode;
  };
};

/**
 * Maps the loyalty connection status onto the presentation state built in IOGZ-450.
 *
 * Fails closed: anything that is not a usable status resolves to `undefined` (no jö UI) or to the
 * error state, never to a card that offers an action which cannot work.
 *
 * jö only ever reports `NoConnection` and `ConnectionEstablished`; the two Payback-only states
 * (`ConnectionInactive`, `ReauthenticationNeeded`) map onto `disconnected`, because for jö the only
 * meaningful next step is connecting again.
 */
export const resolveJoeState = ({
  isGateOpen,
  hasTransportError,
  isStatusRefreshing,
  isReturnPending,
  result,
  isEstablishing,
  hasEstablishFailed,
  notifications,
}: ResolveJoeStateInput): JoePresentationState | undefined => {
  if (!isGateOpen) {
    return undefined;
  }

  // Finishing the round-trip outranks the stale status: the customer clicked connect and must not
  // see the disconnected card flash before the fresh status arrives. The refresh that follows the
  // mutation is the same window - `isEstablishing` is already false while the old status is still
  // the one in hand.
  if (isEstablishing || isStatusRefreshing || isReturnPending) {
    return { status: 'loading' };
  }

  if (hasTransportError) {
    return { status: 'error', notification: notifications.statusError };
  }

  if (result?.__typename === 'CustomerServerFailure') {
    return INELIGIBLE_FAILURE_TYPES.includes(result.failureType)
      ? undefined
      : { status: 'error', notification: notifications.statusError };
  }

  // Paused or not resolved yet. Checked positively because the generated `__typename` is optional,
  // so excluding the failure member alone would not narrow to the payload.
  if (result?.__typename !== 'CustomerLoyaltyConnectionStatusPayload') {
    return { status: 'loading' };
  }

  if (result.status === 'ConnectionEstablished') {
    return { status: 'connected' };
  }

  return hasEstablishFailed
    ? {
        status: 'disconnected',
        notification: { severity: 'warning', content: notifications.connectError },
      }
    : { status: 'disconnected' };
};
